import { Money } from '@afterpay/types'

export const CreditFacilityStatuses = {
  Unknown: 'UNKNOWN', // An UNKNOWN status indicates Core encountered an error contacting the Credit Facility service
  None: 'NONE',
  Open: 'OPEN',
  Closed: 'CLOSED',
} as const
export type CreditFacilityStatus = (typeof CreditFacilityStatuses)[keyof typeof CreditFacilityStatuses]

export const CreditFacilityDocumentTypes = {
  PCLStatement: 'PCL_STATEMENT', // Optional
  CreditContract: 'CREDIT_CONTRACT', // Required
  CreditAssessment: 'CREDIT_ASSESSMENT', // Required
} as const
export type CreditFacilityDocumentType = (typeof CreditFacilityDocumentTypes)[keyof typeof CreditFacilityDocumentTypes]

export interface CreditFacilityDocument {
  id: string
  type: CreditFacilityDocumentType
  url: string | null // A null url indicates Credit Facility service encountered an error contacting the Templating Engine service
}

export interface CreditFacility {
  id: string
  limit: Money
  openedDate: string
  status: CreditFacilityStatus
  documents: CreditFacilityDocument[]
  decreaseLimit: boolean
}

export interface GetCreditFacilityResponse {
  creditFacility: CreditFacility
}

export interface GetCreditFacilitySummaryResponse {
  facilityLimit: Money
  totalOwedPrincipal: Money
  availableToSpend: Money
}

export interface PreAssesmentRequest {
  token: string
}

export interface PreAssessmentResponse {
  facilityLimit: Money
}

export interface CreateFacilityRequest {
  token: string
}

export interface IncomeSource {
  id: string
  latestTransactionAmount: Money
  latestTransactionDate: string
  category: IncomeSourceCategory
}

export interface IncomeSources {
  salary?: IncomeSource[]
  benefits?: IncomeSource[]
  childSuppport?: IncomeSource[]
}
export interface CreateFacilityAssessmentResponse {
  facilityLimit: Money
  creditContractDocumentId: string
  aprRangeMin: Number
  aprRangeMax: Number
  incomeSources?: IncomeSources
}

export interface CreateFacilityAssessmentRequest {
  token: string
  maxFacilityLimit?: Money
  employmentStatus?: EmploymentStatus
  changeInCircumstances?: Boolean
  profilingSessionId: string
  incomeSourceIds?: string[]
}

export interface FacilityIncomeAssessmentRequest {
  token: string
  incomeFrequency?: IncomeFrequency
  maxFacilityLimit: Money
  netIncome?: Money
  otherIncome?: Money
  employmentStatus: EmploymentStatus
  changeInCircumstances?: boolean
}

export interface FacilityExpensesAssessmentRequest {
  token: string
  residentialStatus: ResidentialStatus
  noOfDependents: HouseholdDependants
  monthlyHousingCosts: Money
  monthlyLivingExpenses: Money
  otherMonthlyLoanRepayments: Money
}

export enum IncomeSourceCategory {
  Salary = 'salary',
  Benefits = 'benefits',
  ChildSupport = 'child_support',
}

export enum EmploymentStatus {
  FullTime = 'FT',
  PartTime = 'PT',
  Casual = 'CA',
  SelfEmployed = 'SE',
  NotEmployed = 'UN',
}

export enum IncomeFrequency {
  WEEKLY = 'WEEKLY',
  FORTNIGHTLY = 'FORTNIGHTLY',
  MONTHLY = 'MONTHLY',
}

export enum ResidentialStatus {
  REN = 'REN', // Renting
  HMO = 'HMO', // Homeowner - mortgage
  HNM = 'HNM', // Homeowner - no mortgage
  LFP = 'LPF', // Living with Parents/Family
  SAC = 'SAC', // Shared accomodation
}

export enum HouseholdDependants {
  NONE = 'NONE',
  ONE = 'ONE',
  TWO = 'TWO',
  THREE_PLUS = 'THREE_PLUS',
}

export interface GetCloseCreditFacilityEligibilityResponse {
  eligible: boolean
}

export interface ClosePayMonthlyRequest {
  closeReason: SelectClosePayMonthlyReasonProps
}

export const SELECT_CLOSE_PAY_MONTHLY_REASONS = {
  CLOSE_PAY_MONTHLY: 'CLOSE_PAY_MONTHLY',
  BREAK_FROM_AFTERPAY: 'BREAK_FROM_AFTERPAY',
  APPLYING_FOR_MORTGAGE: 'APPLYING_FOR_MORTGAGE',
  UNABLE_TO_SHOP_RIGHT_NOW: 'UNABLE_TO_SHOP_RIGHT_NOW',
} as const

export type SelectClosePayMonthlyReasonType = typeof SELECT_CLOSE_PAY_MONTHLY_REASONS
export type SelectClosePayMonthlyReasonKeys = keyof SelectClosePayMonthlyReasonType
export type SelectClosePayMonthlyReasonProps = SelectClosePayMonthlyReasonType[SelectClosePayMonthlyReasonKeys]
